/**
 * Bitfinex API Keys Page
 * Manage Bitfinex API credentials for bot trading
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Layout from '../components/Layout'
import { bitfinexApiKeysService } from '../services/bitfinex-api-keys.service'
import type { BitfinexApiKey, BitfinexApiKeyPermission } from '../services/bitfinex-api-keys.service'

export default function BitfinexApiKeysPage() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const queryClient = useQueryClient()

  const { data: apiKeys = [], isLoading } = useQuery({
    queryKey: ['bitfinexApiKeys'],
    queryFn: () => bitfinexApiKeysService.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data: { label?: string; api_key: string; api_secret: string }) =>
      bitfinexApiKeysService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitfinexApiKeys'] })
      setShowCreateModal(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { label?: string | null } }) =>
      bitfinexApiKeysService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitfinexApiKeys'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bitfinexApiKeysService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitfinexApiKeys'] })
    },
  })

  const handleDelete = (id: number, label: string | null) => {
    if (window.confirm(`確定要刪除 API Key "${label || '未命名'}" 嗎？\n\n注意：使用此 API Key 的 Bot 將無法運行。`)) {
      deleteMutation.mutate(id)
    }
  }

  const handleUpdateLabel = (id: number, currentLabel: string | null) => {
    const newLabel = window.prompt('輸入新的標籤名稱：', currentLabel || '')
    if (newLabel !== null) {
      updateMutation.mutate({ id, data: { label: newLabel || null } })
    }
  }

  const activeCount = apiKeys.filter((key) => key.status === 'active').length

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Bitfinex API Keys</h1>
            <p style={styles.subtitle}>
              管理 Bitfinex 交易所 API 憑證 · {activeCount} 個有效
            </p>
          </div>
          <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
            + 新增 API Key
          </button>
        </div>

        {/* Security Warning */}
        <div style={styles.warningBox}>
          <div style={styles.warningIcon}>🔒</div>
          <div style={styles.warningContent}>
            <h3 style={styles.warningTitle}>安全提醒</h3>
            <p style={styles.warningText}>
              您的 API Key 和 Secret 將使用 AES-256-GCM 加密儲存。請確保您的 Bitfinex API Key 具有適當的權限設定（僅需要 Funding 相關權限）。
            </p>
          </div>
        </div>

        {isLoading ? (
          <div style={styles.loading}>載入中...</div>
        ) : apiKeys.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🔑</div>
            <h2 style={styles.emptyTitle}>尚未新增 Bitfinex API Key</h2>
            <p style={styles.emptyText}>
              新增您的 Bitfinex API 憑證以開始使用自動放貸功能
            </p>
            <button onClick={() => setShowCreateModal(true)} style={styles.emptyButton}>
              + 新增第一個 API Key
            </button>
          </div>
        ) : (
          <div style={styles.keysContainer}>
            {apiKeys.map((apiKey) => (
              <ApiKeyCard
                key={apiKey.id}
                apiKey={apiKey}
                onDelete={handleDelete}
                onUpdateLabel={handleUpdateLabel}
              />
            ))}
          </div>
        )}

        {showCreateModal && (
          <CreateApiKeyModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={(data) => createMutation.mutate(data)}
            isLoading={createMutation.isPending}
            error={createMutation.error?.message}
          />
        )}
      </div>
    </Layout>
  )
}

interface ApiKeyCardProps {
  apiKey: BitfinexApiKey
  onDelete: (id: number, label: string | null) => void
  onUpdateLabel: (id: number, currentLabel: string | null) => void
}

function ApiKeyCard({ apiKey, onDelete, onUpdateLabel }: ApiKeyCardProps) {
  const [verificationResult, setVerificationResult] = useState<{
    isValid: boolean
    hasRequiredPermissions: boolean
    permissions: BitfinexApiKeyPermission[]
    missingPermissions: string[]
  } | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [showPermissions, setShowPermissions] = useState(false)

  const statusConfig = {
    active: { color: '#10b981', text: '有效', icon: '✓' },
    invalid: { color: '#f59e0b', text: '無效', icon: '⚠' },
    revoked: { color: '#ef4444', text: '已撤銷', icon: '✕' },
  }

  const status = statusConfig[apiKey.status]
  const createdDate = new Date(apiKey.created_at).toLocaleString('zh-TW')
  const lastUsedDate = apiKey.last_used_at
    ? new Date(apiKey.last_used_at).toLocaleString('zh-TW')
    : '從未使用'

  const handleVerifyPermissions = async () => {
    setIsVerifying(true)
    setVerificationError(null)
    setVerificationResult(null)

    try {
      const response = await bitfinexApiKeysService.verifyExisting(apiKey.id)
      setVerificationResult({
        isValid: response.result.is_valid,
        hasRequiredPermissions: response.result.has_required_permissions,
        permissions: response.result.permissions,
        missingPermissions: response.result.missing_permissions,
      })
      setShowPermissions(true)
    } catch (err) {
      setVerificationError(err instanceof Error ? err.message : '驗證失敗')
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.cardTitle}>
          <h3 style={styles.cardName}>{apiKey.label || '未命名'}</h3>
          <span
            style={{
              ...styles.statusBadge,
              backgroundColor: status.color,
            }}
          >
            {status.icon} {status.text}
          </span>
        </div>
        <div style={styles.cardActions}>
          <button
            onClick={handleVerifyPermissions}
            disabled={isVerifying || apiKey.status !== 'active'}
            style={styles.verifyCardButton}
            title="檢查權限"
          >
            {isVerifying ? '檢查中...' : '🔍 檢查權限'}
          </button>
          <button
            onClick={() => onUpdateLabel(apiKey.id, apiKey.label)}
            style={styles.actionButton}
            title="編輯標籤"
          >
            ✏️ 編輯
          </button>
          <button
            onClick={() => onDelete(apiKey.id, apiKey.label)}
            style={styles.deleteButton}
            title="刪除"
          >
            🗑️ 刪除
          </button>
        </div>
      </div>

      <div style={styles.cardBody}>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>交易所：</span>
          <span style={styles.infoValue}>{apiKey.exchange}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>建立時間：</span>
          <span style={styles.infoValue}>{createdDate}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>最後使用：</span>
          <span style={styles.infoValue}>{lastUsedDate}</span>
        </div>
        {apiKey.permissions && apiKey.permissions.length > 0 && (
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>權限：</span>
            <div style={styles.permissionsContainer}>
              {apiKey.permissions.map((perm) => (
                <span key={perm} style={styles.permissionTag}>
                  {perm}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Verification Error */}
        {verificationError && (
          <div style={styles.cardErrorBox}>
            ❌ {verificationError}
          </div>
        )}

        {/* Verification Result */}
        {showPermissions && verificationResult && (
          <div style={styles.cardPermissionsSection}>
            <div
              style={{
                ...styles.cardResultBox,
                borderColor: verificationResult.isValid ? '#10b981' : '#ef4444',
                backgroundColor: verificationResult.isValid ? '#f0fdf4' : '#fef2f2',
              }}
            >
              <div style={styles.cardResultHeader}>
                <span style={styles.cardResultTitle}>
                  {verificationResult.isValid ? '✅ API Key 有效' : '❌ API Key 無效'}
                </span>
                {verificationResult.isValid && (
                  <span
                    style={{
                      ...styles.cardResultBadge,
                      backgroundColor: verificationResult.hasRequiredPermissions
                        ? '#10b981'
                        : '#f59e0b',
                    }}
                  >
                    {verificationResult.hasRequiredPermissions
                      ? '✓ 權限完整'
                      : '⚠ 權限不足'}
                  </span>
                )}
              </div>

              {verificationResult.isValid && (
                <>
                  <div style={styles.cardPermissionsList}>
                    {verificationResult.permissions.map((perm) => (
                      <div key={perm.key} style={styles.cardPermissionItem}>
                        <div style={styles.cardPermissionName}>
                          <span style={styles.cardPermissionKey}>{perm.label}</span>
                          {perm.required && (
                            <span style={styles.cardRequiredBadge}>必需</span>
                          )}
                        </div>
                        <div style={styles.cardPermissionAccess}>
                          <span
                            style={{
                              ...styles.cardAccessBadge,
                              color: perm.read ? '#10b981' : '#9ca3af',
                            }}
                          >
                            {perm.read ? '✓' : '✕'} 讀取
                          </span>
                          <span
                            style={{
                              ...styles.cardAccessBadge,
                              color: perm.write ? '#10b981' : '#9ca3af',
                            }}
                          >
                            {perm.write ? '✓' : '✕'} 寫入
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {verificationResult.missingPermissions.length > 0 && (
                    <div style={styles.cardMissingPermissions}>
                      <strong>缺少的權限：</strong>
                      <ul style={styles.cardMissingList}>
                        {verificationResult.missingPermissions.map((perm) => (
                          <li key={perm}>{perm}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <button
              onClick={() => setShowPermissions(false)}
              style={styles.cardHideButton}
            >
              隱藏權限詳情
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

interface CreateApiKeyModalProps {
  onClose: () => void
  onSubmit: (data: { label?: string; api_key: string; api_secret: string }) => void
  isLoading: boolean
  error?: string
}

function CreateApiKeyModal({ onClose, onSubmit, isLoading, error }: CreateApiKeyModalProps) {
  const [formData, setFormData] = useState({
    label: '',
    api_key: '',
    api_secret: '',
  })
  const [verificationResult, setVerificationResult] = useState<{
    isValid: boolean
    hasRequiredPermissions: boolean
    permissions: BitfinexApiKeyPermission[]
    missingPermissions: string[]
  } | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)

  const handleVerify = async () => {
    if (!formData.api_key || !formData.api_secret) {
      setVerificationError('請先輸入 API Key 和 Secret')
      return
    }

    setIsVerifying(true)
    setVerificationError(null)
    setVerificationResult(null)

    try {
      const response = await bitfinexApiKeysService.verify({
        api_key: formData.api_key,
        api_secret: formData.api_secret,
      })

      setVerificationResult({
        isValid: response.result.is_valid,
        hasRequiredPermissions: response.result.has_required_permissions,
        permissions: response.result.permissions,
        missingPermissions: response.result.missing_permissions,
      })
    } catch (err) {
      setVerificationError(err instanceof Error ? err.message : '驗證失敗')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      label: formData.label || undefined,
      api_key: formData.api_key,
      api_secret: formData.api_secret,
    })
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>新增 Bitfinex API Key</h2>
          <button onClick={onClose} style={styles.closeButton}>
            ✕
          </button>
        </div>

        {/* Security Instructions */}
        <div style={styles.instructionsBox}>
          <h4 style={styles.instructionsTitle}>📋 如何建立 Bitfinex API Key：</h4>
          <ol style={styles.instructionsList}>
            <li>登入 Bitfinex，進入 Settings → API Keys</li>
            <li>點擊 "Create New Key"</li>
            <li>
              <strong>僅勾選 Funding 相關權限</strong>（不要給予 Trading 或 Withdraw 權限）
            </li>
            <li>複製 API Key 和 Secret 並貼到下方表單</li>
          </ol>
          <p style={styles.instructionsNote}>
            ⚠️ 注意：API Secret 只會顯示一次，請務必妥善保管。
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              標籤名稱（選填）
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="例如：主要帳戶、測試帳戶"
                style={styles.input}
              />
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              API Key *
              <input
                type="text"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder="輸入您的 Bitfinex API Key"
                required
                style={styles.input}
              />
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              API Secret *
              <input
                type="password"
                value={formData.api_secret}
                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                placeholder="輸入您的 Bitfinex API Secret"
                required
                style={styles.input}
              />
            </label>
          </div>

          {/* Verify Permissions Button */}
          <div style={styles.verifySection}>
            <button
              type="button"
              onClick={handleVerify}
              disabled={isVerifying || !formData.api_key || !formData.api_secret}
              style={styles.verifyButton}
            >
              {isVerifying ? '檢查中...' : '🔍 檢查權限'}
            </button>
            <p style={styles.verifyHint}>
              建議先檢查權限，確保您的 API Key 具有必要的權限
            </p>
          </div>

          {/* Verification Error */}
          {verificationError && (
            <div style={styles.errorBox}>
              ❌ {verificationError}
            </div>
          )}

          {/* Verification Result */}
          {verificationResult && (
            <div
              style={{
                ...styles.resultBox,
                borderColor: verificationResult.isValid ? '#10b981' : '#ef4444',
                backgroundColor: verificationResult.isValid ? '#f0fdf4' : '#fef2f2',
              }}
            >
              <div style={styles.resultHeader}>
                <span style={styles.resultTitle}>
                  {verificationResult.isValid ? '✅ API Key 有效' : '❌ API Key 無效'}
                </span>
                {verificationResult.isValid && (
                  <span
                    style={{
                      ...styles.resultBadge,
                      backgroundColor: verificationResult.hasRequiredPermissions
                        ? '#10b981'
                        : '#f59e0b',
                    }}
                  >
                    {verificationResult.hasRequiredPermissions
                      ? '✓ 權限完整'
                      : '⚠ 權限不足'}
                  </span>
                )}
              </div>

              {verificationResult.isValid && (
                <>
                  <div style={styles.permissionsList}>
                    {verificationResult.permissions.map((perm) => (
                      <div key={perm.key} style={styles.permissionItem}>
                        <div style={styles.permissionName}>
                          <span style={styles.permissionKey}>{perm.label}</span>
                          {perm.required && (
                            <span style={styles.requiredBadge}>必需</span>
                          )}
                        </div>
                        <div style={styles.permissionAccess}>
                          <span
                            style={{
                              ...styles.accessBadge,
                              color: perm.read ? '#10b981' : '#9ca3af',
                            }}
                          >
                            {perm.read ? '✓' : '✕'} 讀取
                          </span>
                          <span
                            style={{
                              ...styles.accessBadge,
                              color: perm.write ? '#10b981' : '#9ca3af',
                            }}
                          >
                            {perm.write ? '✓' : '✕'} 寫入
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {verificationResult.missingPermissions.length > 0 && (
                    <div style={styles.missingPermissions}>
                      <strong>缺少的權限：</strong>
                      <ul style={styles.missingList}>
                        {verificationResult.missingPermissions.map((perm) => (
                          <li key={perm}>{perm}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.formActions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              取消
            </button>
            <button type="submit" disabled={isLoading} style={styles.submitButton}>
              {isLoading ? '建立中...' : '建立 API Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const styles = {
  container: {
    padding: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    color: '#111827',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  createButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  warningBox: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '8px',
    marginBottom: '30px',
  },
  warningIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#92400e',
    margin: '0 0 8px 0',
  },
  warningText: {
    fontSize: '14px',
    color: '#78350f',
    margin: 0,
    lineHeight: '1.5',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    fontSize: '18px',
    color: '#6b7280',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '80px 20px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '30px',
  },
  emptyButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  keysContainer: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    transition: 'box-shadow 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  cardTitle: {
    flex: 1,
  },
  cardName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#ffffff',
    borderRadius: '12px',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    padding: '6px 12px',
    fontSize: '14px',
    color: '#3b82f6',
    backgroundColor: 'transparent',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  deleteButton: {
    padding: '6px 12px',
    fontSize: '14px',
    color: '#ef4444',
    backgroundColor: 'transparent',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoLabel: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500',
    minWidth: '80px',
  },
  infoValue: {
    fontSize: '14px',
    color: '#111827',
  },
  permissionsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
  },
  permissionTag: {
    padding: '2px 8px',
    fontSize: '12px',
    color: '#059669',
    backgroundColor: '#d1fae5',
    borderRadius: '4px',
    fontWeight: '500',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    padding: '8px',
    fontSize: '20px',
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
  },
  instructionsBox: {
    backgroundColor: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  instructionsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e40af',
    margin: '0 0 12px 0',
  },
  instructionsList: {
    margin: '0 0 12px 0',
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#1e3a8a',
    lineHeight: '1.6',
  },
  instructionsNote: {
    fontSize: '13px',
    color: '#ef4444',
    margin: 0,
    fontWeight: '500',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  input: {
    padding: '10px 14px',
    fontSize: '15px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  errorBox: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    fontSize: '14px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  verifySection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    alignItems: 'flex-start',
  },
  verifyButton: {
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#3b82f6',
    backgroundColor: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  verifyHint: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    fontStyle: 'italic' as const,
  },
  resultBox: {
    border: '2px solid',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '8px',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  resultTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#111827',
  },
  resultBadge: {
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#ffffff',
    borderRadius: '12px',
  },
  permissionsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  permissionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  permissionName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  permissionKey: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
  },
  requiredBadge: {
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#dc2626',
    backgroundColor: '#fee2e2',
    borderRadius: '4px',
  },
  permissionAccess: {
    display: 'flex',
    gap: '12px',
  },
  accessBadge: {
    fontSize: '13px',
    fontWeight: '500',
  },
  missingPermissions: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#92400e',
  },
  missingList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  },
  // Card verification styles
  verifyCardButton: {
    padding: '6px 12px',
    fontSize: '14px',
    color: '#3b82f6',
    backgroundColor: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  cardErrorBox: {
    padding: '8px 12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    fontSize: '14px',
    marginTop: '12px',
  },
  cardPermissionsSection: {
    marginTop: '16px',
  },
  cardResultBox: {
    border: '2px solid',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
  },
  cardResultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  cardResultTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#111827',
  },
  cardResultBadge: {
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ffffff',
    borderRadius: '10px',
  },
  cardPermissionsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  cardPermissionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
  },
  cardPermissionName: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  cardPermissionKey: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
  },
  cardRequiredBadge: {
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: '600',
    color: '#dc2626',
    backgroundColor: '#fee2e2',
    borderRadius: '3px',
  },
  cardPermissionAccess: {
    display: 'flex',
    gap: '8px',
  },
  cardAccessBadge: {
    fontSize: '12px',
    fontWeight: '500',
  },
  cardMissingPermissions: {
    marginTop: '12px',
    padding: '8px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#92400e',
  },
  cardMissingList: {
    margin: '6px 0 0 0',
    paddingLeft: '16px',
  },
  cardHideButton: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
} as const
